import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '../../../../routes/Routes';
import { useDrainsStore } from '../../../../hooks/useDrainsStore';
import { BinaryQuestionTemplate } from '@templates';
import { ErrorMessages } from '@types';
import { parseZodErrors, stringFieldSchema } from '@utils';
import { PRIVATE_PROPERTY_SELECT_OPTIONS_ERROR } from '../../../../constants/errors';
import { ZodError } from 'zod';

export const PrivatePropertyIssue: FunctionComponent = () => {
  const navigate = useNavigate();
  const {
    update,
    resetSelective,
    data: { isIssuePrivateProperty, issueType },
  } = useDrainsStore();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const title = 'Is the issue on private property?';

  const subText = (
    <div className="flex flex-col gap-[20px]">
      <p>
        Private property is usually owned by individuals or companies, including
        housing associations.
      </p>
      <p>
        A drain could be on private property if it's in:
        <ul className="mt-2 list-disc pl-6">
          <li>a private street</li>
          <li>a private block of flats</li>
          <li>an office development</li>
          <li>a back or front garden</li>
        </ul>
      </p>
    </div>
  );

  const options = [
    {
      id: 'Yes',
      title: 'Yes',
      labelText: 'Yes',
      isDefault: isIssuePrivateProperty === 'Yes',
      isLabelBold: false,
    },
    {
      id: 'No',
      title: 'No',
      labelText: 'No',
      isDefault: isIssuePrivateProperty === 'No',
      isLabelBold: false,
    },
    {
      id: 'DontKnow',
      title: 'DontKnow',
      labelText: 'I do not know',
      isDefault: isIssuePrivateProperty === 'DontKnow',
      isLabelBold: false,
    },
  ];

  const handleChange = (selection: string) => {
    setErrorMessages([]);
    const updatedSelection = options.filter(
      (type) => type.labelText === selection,
    )[0].id as 'Yes' | 'No' | 'DontKnow';

    if (updatedSelection) {
      update('isIssuePrivateProperty', updatedSelection);
    } else {
      resetSelective();
    }
  };

  const handleFloodingNavigation = () => {
    if (issueType === 'Flooding') {
      navigate(RoutesList.FLOOD_AFFECTING);
    } else {
      navigate(RoutesList.DESCRIBE_ISSUE);
    }
  };

  const handleNavigate = () => {
    switch (isIssuePrivateProperty) {
      case 'Yes':
        navigate(RoutesList.REPORT_TO_PRIVATE_PROPERTY_OWNER);
        break;
      case 'No':
        handleFloodingNavigation();
        break;
      case 'DontKnow':
        handleFloodingNavigation();
        break;
      default:
        return false;
    }
  };

  const buttonPrevious = {
    onClick: () => {
      navigate(RoutesList.ISSUE_TYPE);
    },
  };

  const buttonNext = {
    onClick: async () => {
      try {
        stringFieldSchema(
          'isIssuePrivateProperty',
          PRIVATE_PROPERTY_SELECT_OPTIONS_ERROR.required.message,
        ).parse({
          isIssuePrivateProperty,
        });
        handleNavigate();
      } catch (error) {
        const validationErrors = parseZodErrors(error as ZodError);
        setErrorMessages(
          validationErrors.map((error: ErrorMessages) => ({
            message: PRIVATE_PROPERTY_SELECT_OPTIONS_ERROR.required.message,
            path: error.path,
          })),
        );
      }
    },
  };

  return (
    // Binary Question Template used as a multiple options template
    // React node not accepted in multiple options template for description
    <BinaryQuestionTemplate
      title={title}
      description={subText}
      options={options}
      errorPath="isIssuePrivateProperty"
      errorMessages={errorMessages}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
      onChange={handleChange}
    />
  );
};
